import React, { useState, useEffect, useRef } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { supabase, TABLES } from '../supabase'
import toast from 'react-hot-toast'

const AccessEvent = () => {
  const navigate = useNavigate()
  const [accessCode, setAccessCode] = useState('')
  const [loading, setLoading] = useState(false)
  const [event, setEvent] = useState(null)
  const [judgeName, setJudgeName] = useState('')
  const [joining, setJoining] = useState(false)
  const [showAuthDropdown, setShowAuthDropdown] = useState(false)
  const dropdownRef = useRef(null)
  const { user, signOut } = useAuth()

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowAuthDropdown(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const handleSignOut = async () => {
    try {
      await signOut()
      setShowAuthDropdown(false)
    } catch (error) {
      console.error('Error signing out:', error)
    }
  }

  const handleAccessCodeSubmit = async (e) => {
    e.preventDefault()
    if (!accessCode.trim()) {
      toast.error('Please enter an access code')
      return
    }

    setLoading(true)
    try {
      // First, find the judge and their scores by access code
      const { data: judgeData, error: judgeError } = await supabase
        .from(TABLES.JUDGES)
        .select(`
          *,
          events!inner (
            *,
            criteria (*),
            candidates (*),
            judges (*),
            scores!left (*)
          )
        `)
        .eq('judge_access_code', accessCode.toUpperCase())
        .eq('status', 'active')
        .single()

      if (judgeError || !judgeData) {
        toast.error('Invalid access code or judge not found')
        return
      }

      // Check if the event is active
      if (judgeData.events.status !== 'active') {
        toast.error('This event is not active')
        return
      }

      // Check if judge has submitted any scores
      const judgeScores = judgeData.events.scores.filter(score => score.judge_id === judgeData.id)
      
      // Group scores by criteria to check completion
      const scoresByCriteria = {}
      judgeScores.forEach(score => {
        if (!scoresByCriteria[score.criteria_id]) {
          scoresByCriteria[score.criteria_id] = []
        }
        scoresByCriteria[score.criteria_id].push(score)
      })

      // Check which criteria are complete
      const completedCriteria = new Set()
      judgeData.events.criteria.forEach(criteria => {
        const criteriaScores = scoresByCriteria[criteria.id] || []
        const hasAllCandidatesScored = judgeData.events.candidates.every(candidate =>
          criteriaScores.some(score => score.candidate_id === candidate.id)
        )
        if (hasAllCandidatesScored) {
          completedCriteria.add(criteria.id)
        }
      })

      // If judge already has a name assigned, redirect them directly to scoring
      if (judgeData.name && judgeData.name.trim() !== '') {
        toast.success('Welcome back! Redirecting to scoring...')
        navigate(`/judge/${judgeData.events.id}`, { 
          state: { 
            judgeId: judgeData.id,
            judgeName: judgeData.name,
            event: judgeData.events,
            existingScores: judgeScores,
            completedCriteria: Array.from(completedCriteria)
          } 
        })
        return
      }

      // If judge has no name yet, show the join form
      setEvent(judgeData.events)
    } catch (error) {
      console.error('Error fetching event:', error)
      toast.error('Failed to access event')
    } finally {
      setLoading(false)
    }
  }

  const handleJoinEvent = async () => {
    if (!judgeName.trim()) {
      toast.error('Please enter your name')
      return
    }

    setJoining(true)
    try {
      // Check if name is already used by another judge
      const nameExists = event.judges.some(judge => 
        judge.name && judge.name.trim() === judgeName.trim()
      )

      if (nameExists) {
        toast.error('A judge with this name already exists')
        return
      }

      // Find the judge record that matches the access code
      const { data: judgeData, error: judgeError } = await supabase
        .from(TABLES.JUDGES)
        .select('*')
        .eq('judge_access_code', accessCode.toUpperCase())
        .eq('event_id', event.id)
        .single()

      if (judgeError || !judgeData) {
        toast.error('Invalid access code')
        return
      }

      // First update the judge's basic information
      const { data: updatedJudge, error: updateError } = await supabase
        .from(TABLES.JUDGES)
        .update({ 
          name: judgeName.trim()
        })
        .eq('id', judgeData.id)
        .select()
        .single()

      if (updateError) {
        console.error('Error updating judge:', updateError)
        toast.error('Failed to join event')
        return
      }

      // Then get the complete event data with all relationships
      const { data: updatedEvent, error: eventError } = await supabase
        .from(TABLES.EVENTS)
        .select(`
          *,
          criteria (
            id,
            name,
            percentage,
            event_id
          ),
          candidates (
            id,
            name,
            representation,
            candidate_number,
            event_id
          ),
          judges (
            id,
            name,
            judge_number,
            judge_access_code,
            event_id,
            status
          )
        `)
        .eq('id', event.id)
        .single()

      if (eventError) {
        console.error('Error fetching updated event:', eventError)
        toast.error('Failed to load updated event data')
        return
      }

      toast.success('Successfully joined the event!')
      navigate(`/judge/${event.id}`, { 
        state: { 
          judgeId: updatedJudge.id,
          judgeName: updatedJudge.name,
          event: updatedEvent // Pass the freshly fetched event data
        } 
      })
    } catch (error) {
      console.error('Error joining event:', error)
      toast.error('Failed to join event')
    } finally {
      setJoining(false)
    }
  }

  const resetForm = () => {
    setAccessCode('')
    setEvent(null)
    setJudgeName('')
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-green-50/30 flex flex-col">
      <header className="sticky top-0 z-40 bg-white/90 backdrop-blur-md border-b border-gray-100 shadow-sm">
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center py-4">
            {/* Logo and Brand */}
            <div className="flex items-center">
              <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center mr-3">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <h1 className="text-2xl font-bold text-black">JudgeMe <span className="text-green-600 font-semibold">by Codify</span></h1>
                <p className="text-sm text-gray-600">Event Judging Platform</p>
              </div>
            </div>

            {/* Navigation and Auth */}
            <nav className="flex items-center space-x-6">
              {/* Main Navigation */}
              <div className="hidden md:flex items-center space-x-6">
                <Link to="/" className="text-gray-700 hover:text-green-600 font-medium transition-colors">
                  Home
                </Link>
                <Link to="/access-event" className="text-gray-700 hover:text-green-600 font-medium transition-colors">
                  Join Event
                </Link>
                {user && (
                  <Link to="/admin" className="text-gray-700 hover:text-green-600 font-medium transition-colors">
                    Dashboard
                  </Link>
                )}
              </div>

              {/* Auth Section */}
              <div className="relative" ref={dropdownRef}>
                {user ? (
                  <div className="flex items-center space-x-3">
                    <span className="text-sm text-gray-600 hidden md:block">
                      Welcome, {user.email}
                    </span>
                    <button
                      onClick={() => setShowAuthDropdown(!showAuthDropdown)}
                      className="flex items-center space-x-2 bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                      <span>Account</span>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>

                    {/* Dropdown Menu */}
                    {showAuthDropdown && (
                      <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                        <Link
                          to="/admin"
                          className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          onClick={() => setShowAuthDropdown(false)}
                        >
                          Dashboard
                        </Link>
                        <Link
                          to="/create-event"
                          className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          onClick={() => setShowAuthDropdown(false)}
                        >
                          Create Event
                        </Link>
                        <hr className="my-1" />
                        <button
                          onClick={handleSignOut}
                          className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100"
                        >
                          Sign Out
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center space-x-3">
                    <Link
                      to="/auth"
                      className="text-gray-700 hover:text-green-600 font-medium transition-colors"
                    >
                      Sign In
                    </Link>
                    <Link
                      to="/auth"
                      className="bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 transition-colors font-medium"
                    >
                      Create Account
                    </Link>
                  </div>
                )}
              </div>
            </nav>
          </div>
        </div>
      </header>

      <main className="flex-1 py-12 px-4 relative">
        <div className="absolute inset-0 -z-10 overflow-hidden">
          <div className="absolute -top-40 -right-32 h-96 w-96 rounded-full bg-gradient-to-br from-green-300/20 to-emerald-400/10 blur-3xl animate-pulse"></div>
          <div className="absolute -bottom-32 -left-32 h-96 w-96 rounded-full bg-gradient-to-tr from-blue-300/20 to-cyan-400/10 blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        </div>

        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <div className="relative inline-block mb-6">
              <div className="absolute inset-0 bg-gradient-to-br from-green-400 to-emerald-400 rounded-full blur-xl opacity-30 animate-pulse"></div>
              <div className="relative inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full shadow-xl">
                <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                </svg>
              </div>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold mb-4">
              <span className="bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">Join Event</span>
            </h1>
            <p className="text-lg md:text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
              Enter your judge access code to participate in the judging process
            </p>
          </div>

          <div className="relative group max-w-4xl mx-auto">
            <div className="absolute -inset-1 bg-gradient-to-r from-green-600 to-emerald-600 rounded-3xl blur opacity-20 group-hover:opacity-30 transition duration-1000"></div>
            
            <div className="relative bg-white border border-gray-200 rounded-2xl p-8 md:p-12 shadow-xl">
            {!event ? (
              <div className="max-w-md mx-auto">
                <div className="text-center mb-8">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
                    <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  </div>
                  <h2 className="text-2xl font-bold text-black mb-2">Enter Access Code</h2>
                  <p className="text-gray-600">Provide your unique judge access code to join the event</p>
                </div>

                <form onSubmit={handleAccessCodeSubmit} className="space-y-6">
                  <div>
                    <label htmlFor="accessCode" className="block text-sm font-medium text-black mb-3">
                      <div className="flex items-center">
                        <svg className="w-4 h-4 text-gray-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                        </svg>
                        Judge Access Code *
                      </div>
                    </label>
                    <input
                      id="accessCode"
                      type="text"
                      value={accessCode}
                      onChange={(e) => setAccessCode(e.target.value.toUpperCase())}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg text-center text-lg font-mono tracking-wider focus:ring-2 focus:ring-green-500 focus:border-transparent transition-colors"
                      placeholder="Enter access code"
                      required
                    />
                    <p className="text-xs text-gray-500 mt-2 text-center">Enter the access code provided by the event organizer</p>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white py-3 px-6 rounded-xl font-bold shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 transform hover:scale-105 flex items-center justify-center"
                  >
                    {loading ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Checking Code...
                      </>
                    ) : (
                      <>
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                        </svg>
                        Join Event
                      </>
                    )}
                  </button>

                  <div className="text-center">
                    <Link
                      to="/"
                      className="inline-flex items-center text-green-600 hover:text-green-700 text-sm font-medium transition-colors"
                    >
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                      </svg>
                      Back to Home
                    </Link>
                  </div>
                </form>
              </div>
            ) : (
              /* Event Details and Join Form */
              <div className="max-w-2xl mx-auto">
                <div className="relative">
                  <div className="absolute -inset-1 bg-gradient-to-r from-green-400 to-blue-400 rounded-3xl blur opacity-25"></div>
                  <div className="relative bg-gradient-to-r from-green-50 to-blue-50 rounded-2xl p-8 mb-8 shadow-lg">
                  <div className="text-center">
                    {event.logo_url && (
                      <div className="inline-flex items-center justify-center w-20 h-20 bg-white rounded-full mb-4 shadow-sm">
                        <img 
                          src={event.logo_url} 
                          alt="Event Logo" 
                          className="w-16 h-16 rounded-full object-cover"
                        />
                      </div>
                    )}
                    <h2 className="text-3xl font-bold text-black mb-2">{event.name}</h2>
                    <p className="text-gray-600 mb-6">Ready to join this judging event</p>
                    
                    <div className="grid grid-cols-2 gap-6">
                      <div className="bg-white rounded-xl p-4 shadow-sm">
                        <div className="flex items-center justify-center mb-2">
                          <svg className="w-6 h-6 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                          </svg>
                          <span className="text-2xl font-bold text-green-700">{event.candidates.length}</span>
                        </div>
                        <div className="text-green-600 font-medium">Candidates</div>
                      </div>
                      <div className="bg-white rounded-xl p-4 shadow-sm">
                        <div className="flex items-center justify-center mb-2">
                          <svg className="w-6 h-6 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span className="text-2xl font-bold text-blue-700">{event.judges.filter(j => j.name && j.name.trim() !== '').length}/{event.num_judges}</span>
                        </div>
                        <div className="text-blue-600 font-medium">Judges</div>
                      </div>
                    </div>
                  </div>
                  </div>
                </div>

                {event.judges.filter(j => j.name && j.name.trim() !== '').length < event.num_judges ? (
                  <div className="relative">
                    <div className="absolute -inset-1 bg-gradient-to-r from-purple-600 to-pink-600 rounded-3xl blur opacity-20"></div>
                    <div className="relative bg-white border border-gray-200 rounded-2xl p-8 shadow-lg">
                    <div className="text-center mb-6">
                      <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 rounded-full mb-4">
                        <svg className="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                      </div>
                      <h3 className="text-2xl font-bold text-black mb-2">Join as Judge</h3>
                      <p className="text-gray-600">Enter your name to complete the registration</p>
                    </div>
                    
                    <div className="space-y-6">
                      <div>
                        <label htmlFor="judgeName" className="block text-sm font-medium text-black mb-3">
                          <div className="flex items-center">
                            <svg className="w-4 h-4 text-gray-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                            </svg>
                            Your Name *
                          </div>
                        </label>
                        <input
                          id="judgeName"
                          type="text"
                          value={judgeName}
                          onChange={(e) => setJudgeName(e.target.value)}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition-colors"
                          placeholder="Enter your full name"
                          required
                        />
                      </div>

                      <button
                        onClick={handleJoinEvent}
                        disabled={joining || !judgeName.trim()}
                        className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white py-3 px-6 rounded-xl font-bold shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 transform hover:scale-105 flex items-center justify-center"
                      >
                        {joining ? (
                          <>
                            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Joining...
                          </>
                        ) : (
                          <>
                            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                            </svg>
                            Join Event
                          </>
                        )}
                      </button>
                    </div>
                    </div>
                  </div>
                ) : (
                  <div className="bg-red-50 border border-red-200 rounded-2xl p-8 text-center">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4">
                      <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                      </svg>
                    </div>
                    <h3 className="text-xl font-bold text-red-700 mb-2">Event is Full</h3>
                    <p className="text-red-600">This event has reached the maximum number of judges.</p>
                  </div>
                )}

                <div className="text-center mt-6">
                  <button
                    onClick={resetForm}
                    className="inline-flex items-center text-green-600 hover:text-green-700 text-sm font-medium transition-colors"
                  >
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                    </svg>
                    Try Different Code
                  </button>
                </div>
              </div>
            )}
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}

export default AccessEvent 